import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import { breakpoints } from '@avangard/ui/utils';
import { TextField, TextFieldProps } from '@avangard/ui/core';

import { FormStyled } from '@modules/layout/styled';

import type { UpdateSchoolFormValues } from '@modules/schools/requests';

const Root = styled.div`
    display: flex;
    flex-direction: column;

    & > *:last-child {
        margin-block-start: 24px;
    }
`;

const FormRowThreeChild = styled(FormStyled.FormRow)`
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;

    > * {
        width: calc(33.333333333333% - 16px);

        &:not(:last-child) {
            margin-inline-end: 24px;
        }

        ${breakpoints.down('xs')} {
            width: 100%;

            &:not(:last-child) {
                margin-inline-end: 0;
                margin-block-end: 24px;
            }
    }
`;

const CompanyIdentifiersFormGroup = React.memo((): React.ReactElement => {
    const { t } = useTranslation(['common', 'errors']);

    const { values, errors, handleChange, handleBlur } = useFormikContext<UpdateSchoolFormValues>();

    const commonProps = React.useMemo(
        (): Partial<TextFieldProps> => ({
            fullWidth: true,
            required: true,
            onChange: handleChange,
            onBlur: handleBlur,
            inputProps: {
                // Вырубаем дефолтную валидацию input required, в самом TextField оставляем, т.к. нужна
                // красная звездочка в ui (валидация будет через formik)
                required: false,
            },
        }),
        [handleChange, handleBlur],
    );

    return (
        <Root>
            <FormStyled.FormColumnTitle>
                {t('common:form.labels.requisites.titles.requisites')}
            </FormStyled.FormColumnTitle>

            <FormRowThreeChild>
                <TextField
                    {...commonProps}
                    id='requisites.inn'
                    label={t('common:form.labels.requisites.inn')}
                    placeholder={t('common:form.placeholder.requisites.inn') ?? ''}
                    value={values.requisites?.inn}
                    error={!!errors.requisites?.inn}
                    helperText={errors.requisites?.inn}
                />

                <TextField
                    {...commonProps}
                    id='requisites.kpp'
                    label={t('common:form.labels.requisites.kpp')}
                    placeholder={t('common:form.placeholder.requisites.kpp') ?? ''}
                    value={values.requisites?.kpp}
                    error={!!errors.requisites?.kpp}
                    helperText={errors.requisites?.kpp}
                />

                <TextField
                    {...commonProps}
                    id='requisites.ogrn'
                    label={t('common:form.labels.requisites.ogrn')}
                    placeholder={t('common:form.placeholder.requisites.ogrn') ?? ''}
                    value={values.requisites?.ogrn}
                    error={!!errors.requisites?.ogrn}
                    helperText={errors.requisites?.ogrn}
                />
            </FormRowThreeChild>

            <FormStyled.FormRow>
                <TextField
                    {...commonProps}
                    multiline
                    rows={4}
                    id='requisites.address'
                    label={t('common:form.labels.requisites.address')}
                    placeholder={t('common:form.placeholder.requisites.address') ?? ''}
                    value={values.requisites?.address}
                    error={!!errors.requisites?.address}
                    helperText={errors.requisites?.address}
                />
            </FormStyled.FormRow>
        </Root>
    );
});

export { CompanyIdentifiersFormGroup };
